import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import * as Sentry from '@sentry/react'
import { useAuthStore } from '../store/authStore'
import { toast } from '../store/toastStore'

export const api = axios.create({
  baseURL: '/api/v1',
  headers: { 'Content-Type': 'application/json' },
})

// ── Request interceptor: attach JWT ──────────────────────────────

api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// ── Response interceptor: auto-refresh on 401 ───────────────────

let isRefreshing = false
let pendingQueue: Array<{
  resolve: (token: string) => void
  reject: (err: unknown) => void
}> = []

function processQueue(error: unknown, token: string | null) {
  for (const p of pendingQueue) {
    if (token) p.resolve(token)
    else p.reject(error)
  }
  pendingQueue = []
}

const MUTATION_METHODS = new Set(['post', 'put', 'patch', 'delete'])

api.interceptors.response.use(
  (res: AxiosResponse) => {
    const method = res.config.method?.toLowerCase() ?? ''
    if (MUTATION_METHODS.has(method) && res.data?.message) {
      toast.success(res.data.message)
    }
    return res
  },
  async (error: AxiosError<{ message?: string; error?: string }>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }
    if (!originalRequest) return Promise.reject(error)

    const url = originalRequest.url ?? ''

    // Auth endpoints must never trigger the refresh/redirect flow: a 401 from
    // login/register means "bad credentials", not "expired session".
    const isAuthEndpoint =
      url.includes('/auth/login') || url.includes('/auth/register') || url.includes('/auth/refresh')

    if (error.response?.status !== 401 || originalRequest._retry || isAuthEndpoint) {
      // If this was already a retry after refresh, or not a 401, give up
      if (error.response?.status === 401 && originalRequest._retry) {
        useAuthStore.getState().logout()
        window.location.href = '/auth/login'
      }
      // Capture server errors (5xx) to Sentry for visibility
      if (error.response && error.response.status >= 500) {
        Sentry.captureException(error, {
          tags: { api_url: url, status: error.response.status },
        })
      }
      // Show error toast for all failed requests (except silent 401 redirects
      // and auth 401s, which the login/register pages display inline)
      if (!(isAuthEndpoint && error.response?.status === 401)) {
        const errMsg =
          error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          'Something went wrong'
        toast.error(errMsg)
      }
      return Promise.reject(error)
    }

    // Don't try to refresh if we have no refresh token
    const refreshToken = useAuthStore.getState().refreshToken
    if (!refreshToken) {
      useAuthStore.getState().logout()
      window.location.href = '/auth/login'
      return Promise.reject(error)
    }

    // If a refresh is already in flight, queue this request
    if (isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        pendingQueue.push({ resolve, reject })
      }).then((newToken) => {
        originalRequest.headers.Authorization = `Bearer ${newToken}`
        originalRequest._retry = true
        return api(originalRequest)
      })
    }

    // Attempt token refresh
    isRefreshing = true
    originalRequest._retry = true

    try {
      const res = await axios.post('/api/v1/auth/refresh', {
        refresh_token: refreshToken,
      })

      const { access_token, refresh_token: newRefresh } = res.data
      if (!access_token) throw new Error('No access_token in refresh response')

      // Decode claims from new token to update user info
      const claims = decodeJWTClaims(access_token)
      useAuthStore.getState().setAuth(access_token, newRefresh || refreshToken, {
        id: claims.user_id ?? useAuthStore.getState().user?.id ?? '',
        name: claims.name ?? useAuthStore.getState().user?.name ?? '',
        email: claims.email ?? useAuthStore.getState().user?.email ?? '',
        tenant_id: claims.tenant_id ?? useAuthStore.getState().user?.tenant_id ?? '',
        roles: claims.roles ?? useAuthStore.getState().user?.roles ?? [],
        permissions: claims.permissions ?? useAuthStore.getState().user?.permissions ?? [],
      })

      // Retry all queued requests with new token
      processQueue(null, access_token)

      // Retry the original request
      originalRequest.headers.Authorization = `Bearer ${access_token}`
      return api(originalRequest)
    } catch (refreshError) {
      // Refresh failed — logout
      processQueue(refreshError, null)
      useAuthStore.getState().logout()
      window.location.href = '/auth/login'
      return Promise.reject(refreshError)
    } finally {
      isRefreshing = false
    }
  },
)

// ── Proactive refresh: refresh token before it expires ───────────

let proactiveTimer: ReturnType<typeof setTimeout> | null = null

function scheduleProactiveRefresh() {
  if (proactiveTimer) clearTimeout(proactiveTimer)

  const token = useAuthStore.getState().token
  if (!token) return

  const claims = decodeJWTClaims(token)
  if (!claims.exp) return

  // Refresh 60 seconds before expiry
  const expiresAt = claims.exp * 1000
  const refreshAt = expiresAt - 60_000
  const delay = refreshAt - Date.now()

  if (delay <= 0) {
    // Already expired or about to — refresh immediately
    doProactiveRefresh()
    return
  }

  proactiveTimer = setTimeout(doProactiveRefresh, delay)
}

async function doProactiveRefresh() {
  const refreshToken = useAuthStore.getState().refreshToken
  if (!refreshToken || isRefreshing) return

  try {
    const res = await axios.post('/api/v1/auth/refresh', {
      refresh_token: refreshToken,
    })

    const { access_token, refresh_token: newRefresh } = res.data
    if (!access_token) return

    const claims = decodeJWTClaims(access_token)
    useAuthStore.getState().setAuth(access_token, newRefresh || refreshToken, {
      id: claims.user_id ?? useAuthStore.getState().user?.id ?? '',
      name: claims.name ?? useAuthStore.getState().user?.name ?? '',
      email: claims.email ?? useAuthStore.getState().user?.email ?? '',
      tenant_id: claims.tenant_id ?? useAuthStore.getState().user?.tenant_id ?? '',
      roles: claims.roles ?? useAuthStore.getState().user?.roles ?? [],
      permissions: claims.permissions ?? useAuthStore.getState().user?.permissions ?? [],
    })

    // Schedule next refresh
    scheduleProactiveRefresh()
  } catch {
    // Silent fail — the 401 interceptor will handle it when the token actually expires
  }
}

function decodeJWTClaims(token: string): Record<string, any> {
  try {
    const part = token.split('.')[1]
    if (!part) return {}
    const padded = part.replace(/-/g, '+').replace(/_/g, '/')
    const json = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4))
    return JSON.parse(json)
  } catch {
    return {}
  }
}

// Start proactive refresh when auth state changes
useAuthStore.subscribe((state) => {
  if (state.isAuthenticated && state.token) {
    scheduleProactiveRefresh()
  } else {
    if (proactiveTimer) clearTimeout(proactiveTimer)
  }
})

// Also schedule on initial load (page refresh — token restored from localStorage)
if (useAuthStore.getState().isAuthenticated) {
  scheduleProactiveRefresh()
}
